import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { JobindsatsElement } from '../core/jobindsats-element';
import { accordionStyles } from '../maaling-side/styles/accordion-styles';

@customElement('ji-accordion-element')
export class DimensionAccordionElement extends JobindsatsElement(LitElement) {
    static styles = [
        accordionStyles, css`
        .accordion-button {
            color: inherit;
            padding: 16px 30px;
            line-height: 1;
            word-break: break-word;
            font-size: inherit;
        }
        .nopadding-left {
            padding-left: 0;
        }
        .smallpadding-left {
            padding-left: 10px;
        }
        .nopadding {
            padding: 0px;
        }
        .accordion-content {
            overflow-y: auto;
            padding-left: 0px;
            margin-top: 0px;
            border-left: 1px solid var(--divider-color-open);
            border-right: 1px solid var(--divider-color-open);
        }
        .error {
            border: thin solid var(--main-red);
        }
        .accordion-content.nooverflowy {
            overflow-y: visible;
            border: none;
        }
        .accordion-heading {
            margin-top: 0;
        }
        .icon {
            margin-left: auto;
        }
        .required:after {
            content:"*";
            color: var(--main-red);
        }
        .red-text {
            color: var(--main-red);
        }
        div.accordion-heading {
            margin-block-start: 0;
            margin-block-end: 0;
        }
        .accordion-heading.blue {
            background: var(--main-blue-25);
        }
        .accordion-heading.light-blue {
            background: var(--main-blue-10);
        }
        .borderless {
            border: none;
        }
        .bold {
            font-weight: bold;
        }
        .spot-content-header-margin {
            margin-bottom: 5px; /* To have same margin as defined in Spots.css */
        }
        .horizontal-borders {
            border-top: 1px solid var(--divider-color-closed);
            border-bottom: 1px solid var(--divider-color-closed);
        }
        `
    ];

    static instance = 0;

    @property({
        type: Boolean,
    })
    nooverflowy = false;

    @property({
        type: Boolean,
    })
    required = false;

    @property({
        type: Boolean,
    })
    error = false;

    @property({
        attribute: 'header-background-color',
    })
    headerBackgroundColor = '';

    @property()
    title = '';

    @property({
        attribute: 'heading-level',
    })
    headingLevel = "1";

    @property({
        attribute: 'expand-initially',
        type: Boolean,
    })
    expandInitially = false;

    @state()
    expanded = false;

    @property({
        attribute: 'id',
    })
    id = ++DimensionAccordionElement.instance + 'lit-accordion';

    @property({
        type: Boolean,
    })
    borderless = false;

    @property({
        type: Boolean,
    })
    bold = false;

    @property({
        type: Boolean,
    })
    noPaddingLeft = false;

    @property({
        type: Boolean,
    })
    smallPaddingLeft = false;

    @property({
        attribute: 'suppress-padding',
        type: Boolean
    })
    suppressPadding = false;

    @property({
        attribute: 'use-spot-content-header-margin',
        type: Boolean
    })
    useSpotContentMargin = false;

    @property({
        attribute: 'horizontal-borders',
        type: Boolean
    })
    useHorizontalBorders = false;

    constructor() {
        super();
    }

    connectedCallback() {
        super.connectedCallback();
        if (this.required) {
            this.expanded = true;
        } else {
            this.expanded = this.expandInitially;
        }
        this.handleAccordionClick();
    }

    getHeaderClasses() {
        var classes: string[] = [];
        classes.push("accordion-heading");
        classes.push(this.headerBackgroundColor);
        classes.push("h" + this.headingLevel);
        if(this.borderless) classes.push('borderless');
        if(this.useHorizontalBorders) classes.push('horizontal-borders');
        return classes.join(" ");
    }

    getContentClasses() {
        var classes: string[] = [];
        classes.push("accordion-content");
        classes.push("thin-scrollbar");
        if(this.nooverflowy) classes.push('nooverflowy');
        if(this.borderless) classes.push('borderless');
        if(this.error) classes.push('error');
        return classes.join(" ");
    }

    getButtonClasses() {
        var classes: string[] = [];
        classes.push("accordion-button");
        if(this.noPaddingLeft) classes.push('nopadding-left');
        if(this.smallPaddingLeft) classes.push('smallpadding-left');
        if(this.suppressPadding) classes.push('nopadding');
        if(this.bold) classes.push('bold');
        return classes.join(" ");
    }

    getLabelClasses() {
        var classes: string[] = [];
        if(this.required) classes.push('required');
        if(this.error) classes.push('red-text');
        if(this.useSpotContentMargin) classes.push('spot-content-header-margin');
        if(this.bold) classes.push('bold');
        return classes.join(" ");
    }

    render() {
        return html`
            <div class="${this.getHeaderClasses()}" role="heading" aria-level="${this.headingLevel}">
                <button @click=${this.toggleAccordion} class="${this.getButtonClasses()}" aria-expanded="${this.expanded}" aria-controls="${this.id}">
                    <label class="${this.getLabelClasses()}">
                        ${this.title}
                    </label>
                    <span class="icon glyphicon glyphicon-menu-${this.expanded ? 'up' : 'down'}"></span>
                </button>
            </div>
            <div id="${this.id}" ?hidden=${!this.expanded} class="${this.getContentClasses()}">
                <slot></slot>
            </div>
        `;
    }

    handleAccordionClick() {
        const accordionStatusEvent = new CustomEvent('accordion-status', {
            detail: {
                id : this.id,
                title: this.title,
                expanded : this.expanded

            },
            composed: true,
            bubbles: true,
        });
        this.dispatchEvent(accordionStatusEvent);
    }

    toggleAccordion() {
        this.expanded = !this.expanded;
        this.handleAccordionClick();
        
    }
}